/*eslint-disable no-unused-vars */
/*eslint-disable react-hooks/exhaustive-deps*/
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Badge, Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader,
  Row, Spinner, Table,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';
import LocalstorageService from '../../helpers/localstorage-service';
import { AuthUserInfo, CompanyId } from '../../constants';
import profitabilityService from '../../services/profitability';
import NoRecords from '../../components/Table/NoRecords';

// ─── קבועים ──────────────────────────────────────────────────────────────────
const MONTHS_HE = ['', 'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

const OCCASIONAL_TYPES = ['דלק', 'חניה', 'ציוד', 'שכ"ט עו"ד', 'תיקונים', 'ביטוח', 'אחר'];

// קטגוריות ברירת מחדל — ייטענו דינמית מ-DB
const DEFAULT_CATEGORIES = ['טלפוניה', 'ענן', 'שירותים', 'אחר'];

const TAB_OVERVIEW  = 'overview';
const TAB_SOURCES   = 'sources';
const TAB_SEARCH    = 'search';

// ─── פורמט מספר ──────────────────────────────────────────────────────────────
const fmtCurrency = (v) =>
  (v || 0).toLocaleString('he-IL', { minimumFractionDigits: 2 }) + ' ₪';

// ─── קריאת קובץ כ-Base64 ─────────────────────────────────────────────────────
const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = (e) => {
      const b64 = e.target.result.split(',')[1];
      resolve(b64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// ─── רכיב badge קטגוריה ──────────────────────────────────────────────────────
// צבעים ידועים לקטגוריות מוכרות, fallback צבעוני לקטגוריות חדשות
const CATEGORY_COLORS = {
  'טלפוניה': 'info',
  'ענן':     'primary',
  'שירותים': 'secondary',
  'אחר':     'light',
};
const FALLBACK_COLORS = ['success', 'warning', 'danger', 'dark'];
const getCategoryColor = (category) => {
  if (CATEGORY_COLORS[category]) return CATEGORY_COLORS[category];
  // צבע דטרמיניסטי לפי hash של השם
  let hash = 0;
  for (let i = 0; i < (category || '').length; i++) hash = category.charCodeAt(i) + ((hash << 5) - hash);
  return FALLBACK_COLORS[Math.abs(hash) % FALLBACK_COLORS.length];
};

const CategoryBadge = ({ category }) => {
  const color = getCategoryColor(category);
  return category ? <Badge color={color} className="ms-1 small">{category}</Badge> : null;
};

// ─── רכיב עליית קובץ ─────────────────────────────────────────────────────────
const FileUploadField = ({ onFileSelected, uploading, currentFile }) => {
  const fileRef = useRef(null);

  const handleChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowed.includes(file.type)) {
      toast.warning('נא לבחור קובץ PDF, JPG או PNG בלבד');
      return;
    }
    onFileSelected(file);
    e.target.value = '';
  };

  return (
    <div className="d-flex align-items-center gap-2">
      <Button type="button" size="sm" color="outline-secondary"
        onClick={() => fileRef.current?.click()} disabled={uploading}>
        {uploading
          ? <><Spinner size="sm" className="me-1" />מעלה...</>
          : <><i className="ri-attachment-line me-1"></i>
            {currentFile ? 'החלף קובץ' : 'צרף חשבונית'}
          </>}
      </Button>
      {currentFile && (
        <span className="text-success small">
          <i className="ri-check-line me-1"></i>{currentFile}
        </span>
      )}
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
        className="d-none" onChange={handleChange} />
    </div>
  );
};

// ─── טופס הוספת הוצאה ────────────────────────────────────────────────────────
const AddExpenseForm = ({ mode, source, companyId, month, year, createdBy, onSaved, onCancel }) => {
  // mode: 'fixed' | 'occasional'
  const [amount,        setAmount]        = useState('');
  const [expenseType,   setExpenseType]   = useState(OCCASIONAL_TYPES[0]);
  const [description,   setDescription]  = useState('');
  const [invoiceDate,   setInvoiceDate]   = useState('');
  const [blobPath,      setBlobPath]      = useState('');
  const [origFileName,  setOrigFileName]  = useState('');
  const [fileSize,      setFileSize]      = useState(0);
  const [uploading,     setUploading]     = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [filePreviewUrl, setFilePreviewUrl] = useState('');

  const handleFileSelected = async (file) => {
    setUploading(true);
    try {
      const b64 = await readFileAsBase64(file);
      const res = await profitabilityService.uploadExpenseFile({
        companyId, fileContent: b64, fileName: file.name, year, month,
      });
      const data = res.data?.data;
      if (data?.blobPath) {
        setBlobPath(data.blobPath);
        setOrigFileName(data.originalFileName || file.name);
        setFileSize(data.fileSize || file.size);
        setFilePreviewUrl(data.previewUrl || '');
        toast.success('קובץ הועלה בהצלחה');
      }
    } catch {
      toast.error('שגיאה בהעלאת הקובץ');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      toast.warning('נא להזין סכום');
      return;
    }
    setSaving(true);
    try {
      await profitabilityService.saveExpense({
        action:          'INSERT',
        companyId,
        month,
        year,
        expenseType:     mode === 'fixed' ? (source?.sourceName || '') : expenseType,
        amount:          parseFloat(amount),
        description,
        invoiceScanned:  !!blobPath,
        createdBy,
        expenseSourceId: mode === 'fixed' ? source?.id : undefined,
        blobPath:        blobPath || undefined,
        originalFileName: origFileName || undefined,
        fileSize:        fileSize || undefined,
        invoiceDate:     invoiceDate || undefined,
      });
      toast.success('הוצאה נוספה בהצלחה');
      onSaved();
    } catch {
      toast.error('שגיאה בשמירת ההוצאה');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}
      className="border rounded p-3 mb-2 bg-light"
      style={{ direction: 'rtl' }}>
      <Row className="g-2 align-items-end">
        {mode === 'fixed' && source && (
          <Col xs={12}>
            <strong>{source.sourceName}</strong>
            <CategoryBadge category={source.category} />
          </Col>
        )}
        {mode === 'occasional' && (
          <Col xs={12} sm={3}>
            <Label className="mb-1 small">סוג הוצאה</Label>
            <select className="form-select form-select-sm" value={expenseType}
              onChange={e => setExpenseType(e.target.value)}>
              {OCCASIONAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </Col>
        )}
        <Col xs={6} sm={2}>
          <Label className="mb-1 small">סכום ₪</Label>
          <Input bsSize="sm" type="number" min="0" step="0.01"
            value={amount} onChange={e => setAmount(e.target.value)}
            placeholder="0.00" required />
        </Col>
        <Col xs={6} sm={2}>
          <Label className="mb-1 small">תאריך חשבונית</Label>
          <Input bsSize="sm" type="date"
            value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} />
        </Col>
        {mode === 'occasional' && (
          <Col xs={12} sm={3}>
            <Label className="mb-1 small">תיאור</Label>
            <Input bsSize="sm" type="text"
              value={description} onChange={e => setDescription(e.target.value)}
              placeholder="פירוט..." />
          </Col>
        )}
        <Col xs={12} sm="auto">
          <Label className="mb-1 small">קובץ</Label>
          <div>
            <FileUploadField
              onFileSelected={handleFileSelected}
              uploading={uploading}
              currentFile={origFileName || null}
            />
          </div>
        </Col>
        {filePreviewUrl && (
          <Col xs={12} className="mt-2">
            {/\.(jpg|jpeg|png)$/i.test(origFileName)
              ? <img src={filePreviewUrl} alt="תצוגה מקדימה"
                  style={{ maxHeight: 300, maxWidth: '100%', borderRadius: 4, border: '1px solid #dee2e6' }} />
              : <embed src={filePreviewUrl} type="application/pdf"
                  style={{ width: '100%', height: 300, borderRadius: 4, border: '1px solid #dee2e6' }} />
            }
          </Col>
        )}
        <Col xs="auto" className="ms-auto">
          <Button type="submit" color="success" size="sm" disabled={saving || uploading}>
            {saving
              ? <Spinner size="sm" />
              : <><i className="ri-save-line me-1"></i>שמור</>}
          </Button>
          <Button type="button" color="light" size="sm" className="me-2"
            onClick={onCancel}>ביטול</Button>
        </Col>
      </Row>
    </form>
  );
};

// ─── שורת הוצאה בודדת בתוך FixedSourceCard ──────────────────────────────────
const ExpenseRow = ({ exp, companyId, onDelete, onOpenFile }) => {
  const [loadingUrl, setLoadingUrl] = useState(false);

  const handleOpenFile = async () => {
    setLoadingUrl(true);
    try {
      const res = await profitabilityService.getExpenseFileUrl({ id: exp.id, companyId });
      const url = res.data?.data?.url;
      if (url) onOpenFile(url, exp.originalFileName || exp.expenseType || '');
      else toast.warning('לא נמצא קובץ');
    } catch {
      toast.error('שגיאה בפתיחת הקובץ');
    } finally {
      setLoadingUrl(false);
    }
  };

  return (
    <div className="d-flex align-items-center gap-2 mt-1 px-1"
      style={{ fontSize: 13 }}>
      <i className="ri-receipt-line text-success"></i>
      <span className="fw-bold text-success">{fmtCurrency(exp.amount)}</span>
      {exp.description && <span className="text-muted">— {exp.description}</span>}
      {exp.invoiceDate && <span className="text-muted small">{exp.invoiceDate}</span>}
      {exp.blobPath && (
        <button className="btn btn-sm p-0 border-0 bg-transparent"
          onClick={handleOpenFile} disabled={loadingUrl} title="הצג חשבונית">
          {loadingUrl
            ? <Spinner size="sm" />
            : <i className="ri-eye-line text-primary"></i>}
        </button>
      )}
      <button className="btn btn-sm p-0 border-0 bg-transparent ms-auto"
        onClick={() => onDelete(exp.id)} title="מחק">
        <i className="ri-delete-bin-line text-danger small"></i>
      </button>
    </div>
  );
};

// ─── כרטיס ספק קבוע ──────────────────────────────────────────────────────────
const FixedSourceCard = ({ source, expenses, companyId, month, year, createdBy, onRefresh, onDeleteExpense, onOpenFile }) => {
  const [adding, setAdding] = useState(false);

  const hasExpenses  = expenses && expenses.length > 0;
  const totalAmount  = hasExpenses ? expenses.reduce((s, e) => s + (e.amount || 0), 0) : 0;
  const borderColor  = hasExpenses ? '#28a745' : '#dc3545';
  const bgColor      = hasExpenses ? '#f0fff4' : '#fff5f5';

  return (
    <div
      className="rounded p-2 mb-2"
      style={{ border: `2px solid ${borderColor}`, background: bgColor, direction: 'rtl' }}
    >
      {/* שורת כותרת הספק */}
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          {hasExpenses
            ? <i className="ri-checkbox-circle-fill text-success fs-5"></i>
            : <i className="ri-close-circle-fill text-danger fs-5"></i>}
          <span className="fw-semibold">{source.sourceName}</span>
          <CategoryBadge category={source.category} />
          {expenses.length > 1 && (
            <span className="badge bg-info text-dark" style={{ fontSize: 11 }}>
              {expenses.length} חשבוניות
            </span>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          {hasExpenses && (
            <span className="text-success fw-bold small">{fmtCurrency(totalAmount)}</span>
          )}
          {/* כפתור הוסף — תמיד מוצג */}
          {!adding && (
            <Button size="sm" color={hasExpenses ? 'outline-success' : 'outline-primary'}
              onClick={() => setAdding(true)}>
              <i className="ri-add-line me-1"></i>{hasExpenses ? 'הוסף נוסף' : 'הוסף'}
            </Button>
          )}
        </div>
      </div>

      {/* שורות הוצאות קיימות */}
      {hasExpenses && (
        <div className="border-top mt-2 pt-1">
          {expenses.map(exp => (
            <ExpenseRow
              key={exp.id}
              exp={exp}
              companyId={companyId}
              onDelete={onDeleteExpense}
              onOpenFile={onOpenFile}
            />
          ))}
        </div>
      )}

      {/* טופס הוספה */}
      {adding && (
        <div className="mt-2">
          <AddExpenseForm
            mode="fixed"
            source={source}
            companyId={companyId}
            month={month}
            year={year}
            createdBy={createdBy}
            onSaved={() => { setAdding(false); onRefresh(); }}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// רכיב ראשי
// ═══════════════════════════════════════════════════════════════════════════════
const MonthlyExpenses = () => {
  const dispatch = useDispatch();
  const { t }    = useTranslation();
  const [setPageTitle, setCrumbItem] = useOutletContext();

  const companyId  = LocalstorageService.getItem(CompanyId);
  const authUser   = LocalstorageService.getItem(AuthUserInfo);
  const createdBy  = authUser?.data?.id || 0;
  const now        = new Date();

  // ── ניהול מצב ──────────────────────────────────────────────
  const [activeTab,     setActiveTab]     = useState(TAB_OVERVIEW);
  const [month,         setMonth]         = useState(now.getMonth() + 1);
  const [year,          setYear]          = useState(now.getFullYear());

  // Tab 1 — סקירה
  const [allSources,    setAllSources]    = useState([]);  // כל ExpenseSource קבועים
  const [expenses,      setExpenses]      = useState([]);  // כל MonthlyExpense לחודש
  const [showAddOcc,    setShowAddOcc]    = useState(false);

  // Tab 3 — ניהול ספקים
  const [allSourcesMgmt,  setAllSourcesMgmt]  = useState([]);
  const [editSource,      setEditSource]      = useState(null);
  const [showAddSource,   setShowAddSource]   = useState(false);

  // מודלים אישור מחיקה
  const [deleteExpenseId,  setDeleteExpenseId]  = useState(null);
  const [deleteSourceId,   setDeleteSourceId]   = useState(null);

  // Tab חיפוש
  const [searchFromMonth, setSearchFromMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [searchFromYear,  setSearchFromYear]  = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  const [searchToMonth,   setSearchToMonth]   = useState(now.getMonth() + 1);
  const [searchToYear,    setSearchToYear]    = useState(now.getFullYear());
  const [searchResults,   setSearchResults]   = useState([]);
  const [searchLoading,   setSearchLoading]   = useState(false);
  const [searchExecuted,  setSearchExecuted]  = useState(false);

  // קטגוריות דינמיות מ-DB
  const [categories,  setCategories]  = useState(DEFAULT_CATEGORIES);

  // מודל תצוגה מקדימה
  const [previewUrl,       setPreviewUrl]       = useState(null);
  const [previewFileName,  setPreviewFileName]  = useState('');
  const [previewLoading,   setPreviewLoading]   = useState(false);

  // ── כותרות ──────────────────────────────────────────────────
  useEffect(() => {
    setPageTitle(t('PROFITABILITY.EXPENSES_PAGE.PAGE_TITLE'));
    setCrumbItem([
      { title: t('PROFITABILITY.EXPENSES_PAGE.CRUMB_PARENT'), link: '#' },
      { title: t('PROFITABILITY.EXPENSES_PAGE.CRUMB_ITEM'),   link: '#' },
    ]);
  }, []);

  // ── טעינת נתונים ─────────────────────────────────────────────
  // טעינת קטגוריות דינמיות מ-DB
  const fetchCategories = useCallback(async () => {
    try {
      const res = await profitabilityService.getExpenseCategories({ companyId });
      const dbCats = res.data?.dataList || [];
      // מיזוג עם ברירות מחדל — מבטיח ש'אחר' תמיד קיים
      const merged = [...new Set([...dbCats, ...DEFAULT_CATEGORIES])];
      setCategories(merged);
    } catch { /* שקט — fallback לברירת מחדל */ }
  }, [companyId]);

  const fetchAll = useCallback(async () => {
    dispatch(setLoadingOverlay(true));
    try {
      const [srcRes, expRes] = await Promise.all([
        profitabilityService.getExpenseSources({ companyId, includeInactive: false }),
        profitabilityService.getExpenses({ companyId, month, year }),
      ]);
      setAllSources(srcRes.data?.dataList || []);
      setExpenses(expRes.data?.dataList   || []);
    } catch {
      toast.error(t('PROFITABILITY.EXPENSES_PAGE.TOAST_ERROR_LOAD'));
    } finally {
      dispatch(setLoadingOverlay(false));
    }
  }, [companyId, month, year]);

  useEffect(() => {
    fetchCategories();
  }, [companyId]);

  useEffect(() => {
    if (activeTab === TAB_OVERVIEW) fetchAll();
  }, [month, year, activeTab]);

  const fetchSourcesMgmt = useCallback(async () => {
    try {
      const res = await profitabilityService.getExpenseSources({ companyId, includeInactive: true });
      setAllSourcesMgmt(res.data?.dataList || []);
    } catch {
      toast.error('שגיאה בטעינת ספקים');
    }
  }, [companyId]);

  useEffect(() => {
    if (activeTab === TAB_SOURCES) fetchSourcesMgmt();
  }, [activeTab]);

  const fetchSearchResults = useCallback(async () => {
    setSearchLoading(true);
    setSearchExecuted(true);
    try {
      const startYearMonth = searchFromYear * 100 + searchFromMonth;
      const endYearMonth   = searchToYear   * 100 + searchToMonth;
      const res = await profitabilityService.getExpenses({
        companyId,
        month: 0, year: 0,
        startYearMonth,
        endYearMonth,
      });
      setSearchResults(res.data?.dataList || []);
    } catch {
      toast.error('שגיאה בחיפוש הוצאות');
    } finally {
      setSearchLoading(false);
    }
  }, [companyId, searchFromMonth, searchFromYear, searchToMonth, searchToYear]);

  // ── יצוא Excel לתוצאות חיפוש ────────────────────────────────
  const exportSearchExcel = (rows, filename) => {
    const data = rows.map(r => ({
      חודש:           `${r.month}/${r.year}`,
      'ספק/סוג':      r.sourceName || r.expenseType || '',
      קטגוריה:        r.category || '',
      סכום:           r.amount,
      תיאור:          r.description || '',
      'תאריך חשבונית': r.invoiceDate || '',
      'יש קובץ':      r.blobPath ? 'כן' : 'לא',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'הוצאות');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf]), filename);
  };

  // ── מחיקת הוצאה ─────────────────────────────────────────────
  const handleDeleteExpense = (id) => setDeleteExpenseId(id);

  const confirmDeleteExpense = async () => {
    const id = deleteExpenseId;
    setDeleteExpenseId(null);
    try {
      await profitabilityService.saveExpense({ action: 'DELETE', id, companyId, month, year });
      toast.success(t('PROFITABILITY.EXPENSES_PAGE.TOAST_DELETED'));
      fetchAll();
      if (activeTab === TAB_SEARCH && searchExecuted) fetchSearchResults();
    } catch {
      toast.error(t('PROFITABILITY.EXPENSES_PAGE.TOAST_ERROR_DELETE'));
    }
  };

  // ── פתיחת קובץ הוצאה — תצוגה מקדימה ──────────────────────
  const handleOpenExpenseFile = async (expense) => {
    setPreviewFileName(expense.originalFileName || expense.expenseType || '');
    setPreviewLoading(true);
    setPreviewUrl('loading');
    try {
      const res = await profitabilityService.getExpenseFileUrl({ id: expense.id, companyId });
      const url = res.data?.data?.url;
      if (url) setPreviewUrl(url);
      else {
        toast.warning('לא נמצא קובץ');
        setPreviewUrl(null);
      }
    } catch {
      toast.error('שגיאה בפתיחת הקובץ');
      setPreviewUrl(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // ── חישובים ─────────────────────────────────────────────────
  const fixedSources    = allSources.filter(s => s.isFixed);
  const fixedExpenses   = expenses.filter(e => e.expenseSourceId);
  const occasionalExp   = expenses.filter(e => !e.expenseSourceId);

  // מיפוי: expenseSourceId → מערך הוצאות (ספק יכול לקבל מספר חשבוניות בחודש)
  const expBySource = fixedExpenses.reduce((acc, e) => {
    if (!acc[e.expenseSourceId]) acc[e.expenseSourceId] = [];
    acc[e.expenseSourceId].push(e);
    return acc;
  }, {});

  const receivedCount = fixedSources.filter(s => expBySource[s.id]?.length > 0).length;
  const totalFixed    = fixedSources.length;
  const totalAmount   = expenses.reduce((s, r) => s + (r.amount || 0), 0);

  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) yearOptions.push(y);

  // ═══════════════════════════════════════════════════════════════
  // Tab 3 — ניהול ספקים
  // ═══════════════════════════════════════════════════════════════
  const SourceForm = ({ initial, onSave, onCancel }) => {
    const [sourceName,   setSourceName]   = useState(initial?.sourceName   || '');
    const [category,     setCategory]     = useState(initial?.category     || 'אחר');
    const [isCustomCat,  setIsCustomCat]  = useState(false);
    const [customCat,    setCustomCat]    = useState('');
    const [isFixed,      setIsFixed]      = useState(initial?.isFixed !== undefined ? initial.isFixed : true);
    const [isActive,     setIsActive]     = useState(initial?.isActive !== undefined ? initial.isActive : true);
    const [displayOrder, setDisplayOrder] = useState(initial?.displayOrder || 0);
    const [saving,       setSaving]       = useState(false);

    const effectiveCategory = isCustomCat ? customCat.trim() : category;

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!sourceName.trim()) { toast.warning('נא להזין שם ספק'); return; }
      if (isCustomCat && !customCat.trim()) { toast.warning('נא להזין שם קטגוריה'); return; }
      setSaving(true);
      try {
        await profitabilityService.saveExpenseSource({
          action:       initial?.id ? 'UPDATE' : 'INSERT',
          id:           initial?.id,
          companyId,
          sourceName:   sourceName.trim(),
          category:     effectiveCategory,
          isFixed,
          isActive,
          displayOrder: parseInt(displayOrder, 10) || 0,
        });
        toast.success(initial?.id ? 'ספק עודכן' : 'ספק נוסף');
        // רענון קטגוריות אם נוספה חדשה
        if (isCustomCat) fetchCategories();
        onSave();
      } catch {
        toast.error('שגיאה בשמירת ספק');
      } finally {
        setSaving(false);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="border rounded p-3 mb-3 bg-light" style={{ direction: 'rtl' }}>
        <Row className="g-2 align-items-end">
          <Col xs={12} sm={4}>
            <Label className="mb-1 small">שם ספק</Label>
            <Input bsSize="sm" value={sourceName} onChange={e => setSourceName(e.target.value)} required />
          </Col>
          <Col xs={6} sm={3}>
            <Label className="mb-1 small">קטגוריה</Label>
            {isCustomCat ? (
              <div className="d-flex gap-1">
                <Input bsSize="sm" placeholder="שם קטגוריה חדשה" autoFocus
                  value={customCat} onChange={e => setCustomCat(e.target.value)} />
                <Button size="sm" color="light" title="חזור לרשימה"
                  onClick={() => { setIsCustomCat(false); setCustomCat(''); }}>✕</Button>
              </div>
            ) : (
              <select className="form-select form-select-sm" value={category}
                onChange={e => {
                  if (e.target.value === '__NEW__') { setIsCustomCat(true); }
                  else setCategory(e.target.value);
                }}>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                <option value="__NEW__">➕ קטגוריה חדשה...</option>
              </select>
            )}
          </Col>
          <Col xs={6} sm={2}>
            <Label className="mb-1 small">סדר הצגה</Label>
            <Input bsSize="sm" type="number" min="0"
              value={displayOrder} onChange={e => setDisplayOrder(e.target.value)} />
          </Col>
          <Col xs="auto" className="d-flex gap-3 mt-4">
            <div className="form-check">
              <input type="checkbox" className="form-check-input" id="chkFixed"
                checked={isFixed} onChange={e => setIsFixed(e.target.checked)} />
              <label className="form-check-label small" htmlFor="chkFixed">ספק קבוע</label>
            </div>
            <div className="form-check">
              <input type="checkbox" className="form-check-input" id="chkActive"
                checked={isActive} onChange={e => setIsActive(e.target.checked)} />
              <label className="form-check-label small" htmlFor="chkActive">פעיל</label>
            </div>
          </Col>
          <Col xs="auto" className="ms-auto">
            <Button type="submit" color="success" size="sm" disabled={saving}>
              {saving ? <Spinner size="sm" /> : <><i className="ri-save-line me-1"></i>שמור</>}
            </Button>
            <Button type="button" color="light" size="sm" className="me-2" onClick={onCancel}>ביטול</Button>
          </Col>
        </Row>
      </form>
    );
  };

  const handleDeleteSource = (id) => setDeleteSourceId(id);

  const confirmDeleteSource = async () => {
    const id = deleteSourceId;
    setDeleteSourceId(null);
    try {
      await profitabilityService.saveExpenseSource({
        action: 'DELETE', id, companyId, isActive: false,
      });
      toast.success('ספק הושבת');
      fetchSourcesMgmt();
    } catch {
      toast.error('שגיאה בהשבתת ספק');
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════
  return (
    <React.Fragment>
      {/* כותרת + טאבים */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2"
        style={{ direction: 'rtl' }}>
        <ul className="nav nav-tabs" style={{ border: 'none' }}>
          <li className="nav-item">
            <button
              className={`nav-link${activeTab === TAB_OVERVIEW ? ' active' : ''}`}
              onClick={() => setActiveTab(TAB_OVERVIEW)}>
              <i className="ri-eye-line me-1"></i>סקירה
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link${activeTab === TAB_SOURCES ? ' active' : ''}`}
              onClick={() => setActiveTab(TAB_SOURCES)}>
              <i className="ri-building-2-line me-1"></i>ספקים קבועים
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link${activeTab === TAB_SEARCH ? ' active' : ''}`}
              onClick={() => setActiveTab(TAB_SEARCH)}>
              <i className="ri-search-line me-1"></i>חיפוש
            </button>
          </li>
        </ul>
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* Tab 1: סקירה                                           */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeTab === TAB_OVERVIEW && (
        <>
          {/* בוחר חודש/שנה */}
          <Row className="mb-3 align-items-end g-2" style={{ direction: 'rtl' }}>
            <Col xs="auto">
              <label className="form-label mb-1 small">{t('PROFITABILITY.COMMON.MONTH')}</label>
              <select className="form-select form-select-sm" value={month}
                onChange={e => setMonth(+e.target.value)}>
                {MONTHS_HE.slice(1).map((mn, i) => (
                  <option key={i + 1} value={i + 1}>{mn}</option>
                ))}
              </select>
            </Col>
            <Col xs="auto">
              <label className="form-label mb-1 small">{t('PROFITABILITY.COMMON.YEAR')}</label>
              <select className="form-select form-select-sm" value={year}
                onChange={e => setYear(+e.target.value)}>
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </Col>
            <Col xs="auto">
              <span className="badge bg-secondary fs-6">
                סה"כ הוצאות: {fmtCurrency(totalAmount)}
              </span>
            </Col>
          </Row>

          {/* ─── סקציה א: ספקים קבועים ─────────────────────── */}
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2"
              style={{ direction: 'rtl' }}>
              <h6 className="mb-0 fw-bold">
                <i className="ri-building-2-line me-1 text-primary"></i>
                ספקים קבועים
              </h6>
              <div className="d-flex align-items-center gap-2">
                <span className={`badge ${receivedCount === totalFixed ? 'bg-success' : receivedCount === 0 ? 'bg-danger' : 'bg-warning text-dark'}`}>
                  {receivedCount} מתוך {totalFixed} התקבלו
                </span>
                <Button size="sm" color="outline-primary"
                  onClick={() => setActiveTab(TAB_SOURCES)}>
                  <i className="ri-settings-3-line me-1"></i>ניהול ספקים
                </Button>
              </div>
            </div>

            {fixedSources.length === 0 ? (
              <div className="text-muted small p-3 border rounded text-center">
                אין ספקים קבועים — לחץ "ניהול ספקים" להוספה
              </div>
            ) : (
              <div>
                {fixedSources.map(source => (
                  <FixedSourceCard
                    key={source.id}
                    source={source}
                    expenses={expBySource[source.id] || []}
                    companyId={companyId}
                    month={month}
                    year={year}
                    createdBy={createdBy}
                    onRefresh={fetchAll}
                    onDeleteExpense={handleDeleteExpense}
                    onOpenFile={(url, name) => { setPreviewUrl(url); setPreviewFileName(name); }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ─── סקציה ב: הוצאות מזדמנות ─────────────────────── */}
          <div>
            <div className="d-flex align-items-center justify-content-between mb-2"
              style={{ direction: 'rtl' }}>
              <h6 className="mb-0 fw-bold">
                <i className="ri-receipt-line me-1 text-warning"></i>
                הוצאות מזדמנות
              </h6>
              <Button size="sm" color="primary" outline
                onClick={() => setShowAddOcc(v => !v)}>
                <i className="ri-add-line me-1"></i>הוסף הוצאה מזדמנת
              </Button>
            </div>

            {showAddOcc && (
              <AddExpenseForm
                mode="occasional"
                companyId={companyId}
                month={month}
                year={year}
                createdBy={createdBy}
                onSaved={() => { setShowAddOcc(false); fetchAll(); }}
                onCancel={() => setShowAddOcc(false)}
              />
            )}

            {occasionalExp.length === 0 && !showAddOcc ? (
              <div className="text-muted small p-2 border rounded text-center">
                אין הוצאות מזדמנות לחודש זה
              </div>
            ) : (
              occasionalExp.length > 0 && (
                <div className="table-responsive">
                  <Table bordered hover size="sm">
                    <thead className="table-dark">
                      <tr>
                        <th style={{ direction: 'rtl' }}>סוג</th>
                        <th className="text-end">סכום ₪</th>
                        <th style={{ direction: 'rtl' }}>תיאור</th>
                        <th className="text-center">קובץ</th>
                        <th style={{ direction: 'rtl' }}>תאריך</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {occasionalExp.map(r => (
                        <tr key={r.id}>
                          <td>{r.expenseType}</td>
                          <td className="text-end">{fmtCurrency(r.amount)}</td>
                          <td>{r.description}</td>
                          <td className="text-center">
                            {r.blobPath
                              ? (
                                <Button size="sm" color="link" className="p-0"
                                  onClick={() => handleOpenExpenseFile(r)}
                                  title="תצוגה מקדימה">
                                  <i className="ri-eye-line text-primary"></i>
                                </Button>
                              )
                              : <i className="ri-close-circle-line text-muted"></i>}
                          </td>
                          <td className="text-muted small">
                            {r.invoiceDate
                              ? new Date(r.invoiceDate).toLocaleDateString('he-IL')
                              : r.createdDate
                                ? new Date(r.createdDate).toLocaleDateString('he-IL')
                                : ''}
                          </td>
                          <td>
                            <Button size="sm" color="danger" outline
                              onClick={() => handleDeleteExpense(r.id)}>
                              <i className="ri-delete-bin-line"></i>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-secondary fw-bold">
                        <td>סה"כ מזדמנות</td>
                        <td className="text-end">
                          {fmtCurrency(occasionalExp.reduce((s, r) => s + (r.amount || 0), 0))}
                        </td>
                        <td colSpan={4}></td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              )
            )}
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* Tab 3: ניהול ספקים                                     */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeTab === TAB_SOURCES && (
        <div style={{ direction: 'rtl' }}>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="mb-0 fw-bold">
              <i className="ri-building-2-line me-1"></i>ניהול ספקים קבועים
            </h6>
            <Button color="primary" size="sm" onClick={() => { setShowAddSource(true); setEditSource(null); }}>
              <i className="ri-add-line me-1"></i>הוסף ספק
            </Button>
          </div>

          {(showAddSource && !editSource) && (
            <SourceForm
              initial={null}
              onSave={() => { setShowAddSource(false); fetchSourcesMgmt(); }}
              onCancel={() => setShowAddSource(false)}
            />
          )}

          {editSource && (
            <SourceForm
              initial={editSource}
              onSave={() => { setEditSource(null); fetchSourcesMgmt(); }}
              onCancel={() => setEditSource(null)}
            />
          )}

          {allSourcesMgmt.length === 0 ? (
            <NoRecords />
          ) : (
            <div className="table-responsive">
              <Table bordered hover size="sm">
                <thead className="table-dark">
                  <tr>
                    <th>שם ספק</th>
                    <th>קטגוריה</th>
                    <th className="text-center">קבוע</th>
                    <th className="text-center">פעיל</th>
                    <th className="text-center">סדר</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {allSourcesMgmt.map(s => (
                    <tr key={s.id} className={!s.isActive ? 'text-muted' : ''}>
                      <td>
                        {s.sourceName}
                        {!s.isActive && <span className="badge bg-secondary ms-1 small">לא פעיל</span>}
                      </td>
                      <td><CategoryBadge category={s.category} /></td>
                      <td className="text-center">
                        {s.isFixed
                          ? <i className="ri-checkbox-circle-fill text-success"></i>
                          : <i className="ri-close-circle-line text-muted"></i>}
                      </td>
                      <td className="text-center">
                        {s.isActive
                          ? <i className="ri-checkbox-circle-fill text-success"></i>
                          : <i className="ri-close-circle-line text-danger"></i>}
                      </td>
                      <td className="text-center">{s.displayOrder}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button size="sm" color="warning" outline
                            onClick={() => { setEditSource(s); setShowAddSource(false); }}
                            title="עריכה">
                            <i className="ri-pencil-line"></i>
                          </Button>
                          {s.isActive && (
                            <Button size="sm" color="danger" outline
                              onClick={() => handleDeleteSource(s.id)}
                              title="השבת">
                              <i className="ri-forbid-line"></i>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* Tab חיפוש                                               */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeTab === TAB_SEARCH && (
        <div style={{ direction: 'rtl' }}>
          {/* טופס חיפוש */}
          <div className="card p-3 mb-3">
            <Row className="g-2 align-items-end">
              <Col xs="auto">
                <Label className="mb-1 small">מחודש</Label>
                <div className="d-flex gap-1">
                  <select className="form-select form-select-sm" style={{ width: 110 }}
                    value={searchFromMonth} onChange={e => setSearchFromMonth(+e.target.value)}>
                    {MONTHS_HE.slice(1).map((mn, i) => (
                      <option key={i + 1} value={i + 1}>{mn}</option>
                    ))}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 90 }}
                    value={searchFromYear} onChange={e => setSearchFromYear(+e.target.value)}>
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </Col>
              <Col xs="auto">
                <Label className="mb-1 small">עד חודש</Label>
                <div className="d-flex gap-1">
                  <select className="form-select form-select-sm" style={{ width: 110 }}
                    value={searchToMonth} onChange={e => setSearchToMonth(+e.target.value)}>
                    {MONTHS_HE.slice(1).map((mn, i) => (
                      <option key={i + 1} value={i + 1}>{mn}</option>
                    ))}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 90 }}
                    value={searchToYear} onChange={e => setSearchToYear(+e.target.value)}>
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </Col>
              <Col xs="auto">
                <Button color="primary" size="sm" onClick={fetchSearchResults} disabled={searchLoading}>
                  {searchLoading
                    ? <Spinner size="sm" />
                    : <><i className="ri-search-line me-1" />חפש</>}
                </Button>
              </Col>
              {searchResults.length > 0 && (
                <Col xs="auto" className="ms-auto">
                  <Button color="success" outline size="sm"
                    onClick={() => exportSearchExcel(searchResults, `הוצאות-${searchFromMonth}-${searchFromYear}-עד-${searchToMonth}-${searchToYear}.xlsx`)}>
                    <i className="ri-file-excel-line me-1" />Excel יצוא
                  </Button>
                </Col>
              )}
            </Row>
          </div>

          {/* תוצאות */}
          {!searchExecuted ? (
            <div className="text-center text-muted py-4">
              <i className="ri-search-line fs-2 d-block mb-2" />
              <small>הגדר טווח תאריכים ולחץ "חפש"</small>
            </div>
          ) : searchLoading ? null : searchResults.length === 0 ? (
            <div className="text-center text-muted py-4">אין הוצאות בטווח שנבחר</div>
          ) : (
            <>
              <div className="mb-2 d-flex justify-content-between align-items-center">
                <span className="text-muted small">{searchResults.length} רשומות</span>
                <span className="fw-bold">
                  סה"כ: {fmtCurrency(searchResults.reduce((s, r) => s + (r.amount || 0), 0))}
                </span>
              </div>
              <div className="table-responsive">
                <Table bordered hover size="sm" className="text-nowrap">
                  <thead className="table-dark">
                    <tr>
                      <th>חודש</th>
                      <th>ספק / סוג</th>
                      <th>קטגוריה</th>
                      <th className="text-end">סכום</th>
                      <th>תיאור</th>
                      <th>תאריך חשבונית</th>
                      <th className="text-center">קובץ</th>
                      <th className="text-center" style={{ width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map(r => (
                      <tr key={r.id}>
                        <td className="text-muted small">{MONTHS_HE[r.month]} {r.year}</td>
                        <td>
                          <span className="fw-semibold">{r.sourceName || r.expenseType || '—'}</span>
                          {r.isFixed ? <Badge color="success" className="ms-1 small">קבוע</Badge> : ''}
                        </td>
                        <td>{r.category ? <CategoryBadge category={r.category} /> : ''}</td>
                        <td className="text-end fw-bold">{fmtCurrency(r.amount)}</td>
                        <td className="text-muted small">{r.description || ''}</td>
                        <td className="text-muted small">{r.invoiceDate ? r.invoiceDate.substring(0, 10) : '—'}</td>
                        <td className="text-center">
                          {r.blobPath ? (
                            <button className="btn btn-sm p-0 border-0 bg-transparent"
                              onClick={() => handleOpenExpenseFile(r)} title="הצג חשבונית">
                              <i className="ri-eye-line text-primary fs-5"></i>
                            </button>
                          ) : ''}
                        </td>
                        <td className="text-center">
                          <button className="btn btn-sm p-0 border-0 bg-transparent"
                            onClick={() => handleDeleteExpense(r.id)} title="מחק">
                            <i className="ri-delete-bin-line text-danger"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="table-secondary fw-bold">
                      <td colSpan={3}>סה"כ ({searchResults.length})</td>
                      <td className="text-end">{fmtCurrency(searchResults.reduce((s, r) => s + (r.amount || 0), 0))}</td>
                      <td colSpan={4}></td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── מודל מחיקת הוצאה ── */}
      <Modal isOpen={!!deleteExpenseId} toggle={() => setDeleteExpenseId(null)} centered size="sm">
        <ModalHeader toggle={() => setDeleteExpenseId(null)} className="border-0 pb-0">
          <i className="ri-delete-bin-line text-danger me-2" />מחיקת הוצאה
        </ModalHeader>
        <ModalBody className="text-center py-3">
          <i className="ri-alert-line text-danger fs-1 d-block mb-2" />
          <p className="mb-0">{t('PROFITABILITY.EXPENSES_PAGE.CONFIRM_DELETE')}</p>
        </ModalBody>
        <ModalFooter className="border-0 pt-0 justify-content-center gap-2">
          <Button color="danger" onClick={confirmDeleteExpense}>
            <i className="ri-delete-bin-line me-1" />מחק
          </Button>
          <Button color="light" onClick={() => setDeleteExpenseId(null)}>ביטול</Button>
        </ModalFooter>
      </Modal>

      {/* ── מודל השבתת ספק ── */}
      <Modal isOpen={!!deleteSourceId} toggle={() => setDeleteSourceId(null)} centered size="sm">
        <ModalHeader toggle={() => setDeleteSourceId(null)} className="border-0 pb-0">
          <i className="ri-eye-off-line text-warning me-2" />השבתת ספק
        </ModalHeader>
        <ModalBody className="text-center py-3">
          <i className="ri-building-line text-warning fs-1 d-block mb-2" />
          <p className="mb-0">להשבית ספק זה? הוא לא יוצג עוד בסקירה החודשית.</p>
        </ModalBody>
        <ModalFooter className="border-0 pt-0 justify-content-center gap-2">
          <Button color="warning" onClick={confirmDeleteSource}>
            <i className="ri-eye-off-line me-1" />השבת
          </Button>
          <Button color="light" onClick={() => setDeleteSourceId(null)}>ביטול</Button>
        </ModalFooter>
      </Modal>

      {/* ── מודל תצוגה מקדימה ── */}
      <Modal isOpen={!!previewUrl} toggle={() => setPreviewUrl(null)} size="xl" centered scrollable>
        <ModalHeader toggle={() => setPreviewUrl(null)} className="border-bottom py-2">
          <i className="ri-file-text-line me-2 text-primary" />
          <span className="text-truncate" style={{ maxWidth: 500, display: 'inline-block' }}>
            {previewFileName || 'חשבונית'}
          </span>
          {previewUrl && previewUrl !== 'loading' && (
            <Button size="sm" color="link" className="ms-3 text-muted p-0"
              onClick={() => window.open(previewUrl, '_blank')} title="פתח בטאב חדש">
              <i className="ri-external-link-line" />
            </Button>
          )}
        </ModalHeader>
        <ModalBody style={{ height: '80vh', padding: 0, background: '#f5f5f5' }}>
          {previewUrl === 'loading' || previewLoading ? (
            <div className="d-flex align-items-center justify-content-center h-100">
              <div className="text-center text-muted">
                <i className="ri-loader-4-line fs-1 d-block mb-2" />טוען...
              </div>
            </div>
          ) : previewUrl && /\.(jpg|jpeg|png)$/i.test(previewFileName) ? (
            <div className="d-flex align-items-center justify-content-center h-100 p-3">
              <img src={previewUrl} alt="חשבונית"
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }} />
            </div>
          ) : previewUrl ? (
            <embed src={previewUrl} type="application/pdf"
              style={{ width: '100%', height: '100%', border: 'none' }} />
          ) : null}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default MonthlyExpenses;
